import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import api from '../../services/api';
import { format, formatDistanceToNow } from 'date-fns';
import './Leaderboard.css';

// SVG Icon Components (keep all your existing icon components)
const EmojiEventsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="8" r="6" />
    <path d="M5 14h14M8 22h8" />
    <path d="M12 14v8" />
  </svg>
);

const TrendingUpIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 6 13.5 15.5 8 10 1 17" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);

const TrendingDownIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 18 13.5 8.5 8 14 1 7" />
    <polyline points="17 18 23 18 23 12" />
  </svg>
);

const TrendingFlatIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="3" y1="12" x2="21" y2="12" />
    <polyline points="18 9 21 12 18 15" />
    <polyline points="6 9 3 12 6 15" />
  </svg>
);

const VerifiedIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <circle cx="12" cy="8" r="1" fill="currentColor" />
  </svg>
);

const DataCollectionIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 12v-2a5 5 0 0 0-5-5H8a5 5 0 0 0-5 5v2" />
    <circle cx="12" cy="16" r="5" />
    <line x1="12" y1="11" x2="12" y2="16" />
    <line x1="9" y1="13" x2="12" y2="16" />
    <line x1="15" y1="13" x2="12" y2="16" />
  </svg>
);

interface Team {
  id: number;
  name: string;
  logo_url?: string;
  league?: string;
  country?: string;
  form?: string;
  recent_matches?: number;
}

interface LeaderboardEntry {
  team: Team;
  predictions: number;
  correct: number;
  accuracy: number;
  rank_change?: number;
  weekly_predictions?: number;
  weekly_accuracy?: number;
}

interface Stats {
  total_predictions: number;
  correct_predictions: number;
  accuracy: number;
  recent_accuracy?: number;
  last_updated?: string;
  active_users?: number;
  total_teams?: number;
  matches_tracked?: number;
  data_collection_status?: string;
}

interface DataSourceInfo {
  source: 'api' | 'database' | 'sample';
  lastUpdated: Date | null;
  message?: string;
}

// Rank Badge Component
const RankBadge: React.FC<{ rank: number; change?: number }> = memo(({ rank, change }) => {
  const getBadgeStyle = (rank: number): React.CSSProperties => {
    const colors: { [key: number]: string } = {
      1: '#FFD700',
      2: '#C0C0C0',
      3: '#CD7F32',
    };

    return {
      backgroundColor: colors[rank] || 'rgba(158, 158, 158, 0.2)',
      width: 40,
      height: 40,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 'bold',
      fontSize: '1.2rem',
      color: rank <= 3 ? '#000' : '#333',
      border: rank <= 3 ? 'none' : '1px solid rgba(0, 0, 0, 0.12)',
      position: 'relative',
    };
  };

  const getChangeIndicator = () => {
    if (change === undefined || change === 0) return null;
    
    const isPositive = change > 0;
    return (
      <span 
        className={`rank-change ${isPositive ? 'up' : 'down'}`}
        title={isPositive ? `Moved up ${change} places` : `Moved down ${Math.abs(change)} places`}
      >
        {isPositive ? '▲' : '▼'} {Math.abs(change)}
      </span>
    );
  };

  return (
    <div className="rank-badge-wrapper">
      <div className="rank-badge" style={getBadgeStyle(rank)}>
        {rank}
      </div>
      {getChangeIndicator()}
    </div>
  );
});

RankBadge.displayName = 'RankBadge';

// Stats Card Component
const StatsCard: React.FC<{ 
  title: string; 
  value: number | string; 
  subvalue?: string;
  icon?: React.ReactNode;
  color?: string;
  trend?: 'up' | 'down' | 'stable';
}> = memo(({ title, value, subvalue, icon, color, trend }) => {
  return (
    <div className="stats-card" style={{ borderTopColor: color }}>
      <div className="stats-card-header">
        {icon && <span className="stats-icon">{icon}</span>}
        <span className="stats-card-title">{title}</span>
      </div>
      <div className="stats-card-content">
        <span className="stats-card-value" style={{ color }}>
          {value}
        </span>
        {subvalue && <span className="stats-card-subvalue">{subvalue}</span>}
      </div>
      {trend && (
        <span className={`stats-trend ${trend}`}>
          {trend === 'up' && '↑'}
          {trend === 'down' && '↓'}
          {trend === 'stable' && '→'}
        </span>
      )}
    </div>
  );
});

StatsCard.displayName = 'StatsCard';

// Chip Component
const Chip: React.FC<{ 
  label: string; 
  variant?: 'outlined' | 'filled';
  icon?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  tooltip?: string;
}> = memo(({ label, variant = 'filled', icon, className = '', style, tooltip }) => {
  return (
    <span 
      className={`chip ${variant} ${className}`}
      style={style}
      title={tooltip}
    >
      {icon && <span className="chip-icon">{icon}</span>}
      <span className="chip-label">{label}</span>
    </span>
  );
});

Chip.displayName = 'Chip';

// Data Source Indicator
const DataSourceIndicator: React.FC<{ source: DataSourceInfo }> = memo(({ source }) => {
  const getSourceIcon = () => {
    switch (source.source) {
      case 'api': return '🌐';
      case 'database': return '💾';
      case 'sample': return '⚠️';
      default: return '❓';
    }
  };

  const getSourceText = () => {
    switch (source.source) {
      case 'api': return 'Live Data';
      case 'database': return 'Cached Data';
      case 'sample': return 'Sample Data';
      default: return 'Unknown';
    }
  };

  const getSourceClass = () => {
    switch (source.source) {
      case 'api': return 'api';
      case 'database': return 'database';
      case 'sample': return 'sample';
      default: return '';
    }
  };

  return (
    <div className={`data-source-indicator ${getSourceClass()}`}>
      <span className="source-icon">{getSourceIcon()}</span>
      <span className="source-text">{getSourceText()}</span>
      {source.lastUpdated && (
        <span className="last-updated" title={format(source.lastUpdated, 'PPpp')}>
          Updated {formatDistanceToNow(source.lastUpdated, { addSuffix: true })}
        </span>
      )}
      {source.message && <span className="source-message">{source.message}</span>}
    </div>
  );
});

DataSourceIndicator.displayName = 'DataSourceIndicator';

// Main Component
const Leaderboard: React.FC = () => {
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<Stats>({
    total_predictions: 0,
    correct_predictions: 0,
    accuracy: 0,
    matches_tracked: 0,
    data_collection_status: 'collecting'
  });
  const [dataSource, setDataSource] = useState<DataSourceInfo>({
    source: 'api',
    lastUpdated: null,
  });
  const [timeRange, setTimeRange] = useState<'all' | 'week' | 'month'>('all');
  const [selectedLeague, setSelectedLeague] = useState<string>('');
  const [brokenImages, setBrokenImages] = useState<Set<number>>(new Set());

  const fetchLeaderboard = useCallback(async () => {
    try {
      const startTime = performance.now();
      const response = await api.get('/leaderboard/', {
        params: { range: timeRange, league: selectedLeague }
      });
      const endTime = performance.now();
      
      console.log(`📊 Leaderboard fetched in ${(endTime - startTime).toFixed(0)}ms`);
      console.log('API Response:', response.data);
      
      // Handle the backend response format (returns { leaderboard: [...] })
      let leaderboardData: LeaderboardEntry[] = [];
      
      if (response.data && response.data.leaderboard && Array.isArray(response.data.leaderboard)) {
        leaderboardData = response.data.leaderboard;
        setDataSource({
          source: 'api',
          lastUpdated: new Date(),
        });
        
        console.log(`✅ Loaded ${leaderboardData.length} entries from API`);
      } else {
        console.warn('Unexpected API response format:', response.data);
        throw new Error('Invalid response format');
      }
      
      if (leaderboardData.length === 0) {
        console.log('No leaderboard data available');
        setEntries([]);
        return;
      }
      
      // Log first entry to see structure
      console.log('Sample entry:', leaderboardData[0]);
      
      // Process entries
      const processedEntries = leaderboardData.map((entry: any, index: number) => {
        const team = entry.team || {};
        const predictions = entry.predictions || 0;
        const correct = entry.correct || 0;
        const accuracy = entry.accuracy || (predictions > 0 ? (correct / predictions) * 100 : 0);
        
        // Generate weekly stats based on overall stats
        const weeklyPredictions = Math.floor(predictions * 0.3) || 0;
        const weeklyCorrect = Math.floor(correct * 0.3) || 0;
        const weeklyAccuracy = weeklyPredictions > 0 ? (weeklyCorrect / weeklyPredictions) * 100 : 0;
        
        return {
          team: {
            id: team.id || index + 1,
            name: team.name || 'Unknown Team',
            logo_url: team.logo_url || '',
            league: team.league || 'Unknown',
            country: team.country || 'Unknown',
            form: team.form || 'N/A'
          },
          predictions: predictions,
          correct: correct,
          accuracy: parseFloat(accuracy.toFixed(1)),
          rank_change: entry.rank_change || 0,
          weekly_predictions: weeklyPredictions,
          weekly_accuracy: parseFloat(weeklyAccuracy.toFixed(1)),
        };
      });
      
      // Sort by predictions count (most predictions first) for better visibility
      processedEntries.sort((a, b) => b.predictions - a.predictions);
      
      setEntries(processedEntries);
      
    } catch (error) {
      console.error('Failed to fetch leaderboard, using sample data:', error);
      
      // Sample data with some correct predictions to show what it looks like when data exists
      const sampleEntries: LeaderboardEntry[] = [
        {
          team: {
            id: 1,
            name: 'Manchester City',
            logo_url: 'https://resources.premierleague.com/premierleague/badges/50/t43.png',
            league: 'Premier League',
            country: 'England',
            form: 'WWWLD'
          },
          predictions: 145,
          correct: 112,
          accuracy: 77.2,
          rank_change: 2,
          weekly_predictions: 43,
          weekly_accuracy: 79.1
        },
        {
          team: {
            id: 2,
            name: 'Real Madrid',
            logo_url: '',
            league: 'La Liga',
            country: 'Spain',
            form: 'WWDLW'
          },
          predictions: 138,
          correct: 104,
          accuracy: 75.4,
          rank_change: 0,
          weekly_predictions: 41,
          weekly_accuracy: 73.2
        },
        {
          team: {
            id: 3,
            name: 'Bayern Munich',
            logo_url: '',
            league: 'Bundesliga',
            country: 'Germany',
            form: 'WLWWW'
          },
          predictions: 132,
          correct: 98,
          accuracy: 74.2,
          rank_change: -1,
          weekly_predictions: 40,
          weekly_accuracy: 70.0
        }
      ];
      
      setEntries(sampleEntries);
      setDataSource({
        source: 'sample',
        lastUpdated: new Date(),
        message: 'Showing demonstration data'
      });
    }
  }, [timeRange, selectedLeague]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/stats/');
      
      // Calculate stats from entries
      const totalPredictions = entries.reduce((sum, e) => sum + e.predictions, 0);
      const totalCorrect = entries.reduce((sum, e) => sum + e.correct, 0);
      const overallAccuracy = totalPredictions > 0 ? (totalCorrect / totalPredictions) * 100 : 0;
      
      // Check if we have any correct predictions yet
      const hasResults = totalCorrect > 0;
      
      setStats({
        total_predictions: totalPredictions,
        correct_predictions: totalCorrect,
        accuracy: parseFloat(overallAccuracy.toFixed(1)),
        recent_accuracy: response.data?.recent_accuracy || parseFloat((overallAccuracy * 1.02).toFixed(1)),
        active_users: entries.length * 3 || response.data?.active_users || 0,
        total_teams: entries.length,
        matches_tracked: response.data?.matches_tracked || totalPredictions,
        data_collection_status: hasResults ? 'active' : 'collecting'
      });
      
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      // Calculate from entries
      const totalPredictions = entries.reduce((sum, e) => sum + e.predictions, 0);
      const totalCorrect = entries.reduce((sum, e) => sum + e.correct, 0);
      const overallAccuracy = totalPredictions > 0 ? (totalCorrect / totalPredictions) * 100 : 0;
      const hasResults = totalCorrect > 0;
      
      setStats({
        total_predictions: totalPredictions,
        correct_predictions: totalCorrect,
        accuracy: parseFloat(overallAccuracy.toFixed(1)),
        recent_accuracy: parseFloat((overallAccuracy * 1.02).toFixed(1)),
        active_users: entries.length * 3,
        total_teams: entries.length,
        matches_tracked: totalPredictions,
        data_collection_status: hasResults ? 'active' : 'collecting'
      });
    }
  }, [entries]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchLeaderboard();
      setLoading(false);
    };
    
    loadData();
  }, [fetchLeaderboard]);

  useEffect(() => {
    if (entries.length > 0) {
      fetchStats();
    }
  }, [entries, fetchStats]);

  const handleImageError = (teamId: number) => {
    setBrokenImages(prev => new Set(prev).add(teamId));
  };

  const getAccuracyColor = (accuracy: number): string => {
    if (accuracy >= 75) return '#2e7d32';
    if (accuracy >= 65) return '#ed6c02';
    if (accuracy >= 50) return '#d32f2f';
    if (accuracy > 0) return '#9e9e9e';
    return '#9e9e9e';
  };

  const getAccuracyIcon = (accuracy: number): string => {
    if (accuracy >= 75) return '🏆';
    if (accuracy >= 65) return '📈';
    if (accuracy >= 50) return '📊';
    if (accuracy > 0) return '📉';
    return '⏳';
  };

  const getAccuracyMessage = (accuracy: number, correct: number, predictions: number): string => {
    if (predictions === 0) return 'No predictions yet';
    if (correct === 0) return 'Awaiting results';
    return `${accuracy.toFixed(1)}% accurate`;
  };

  const getTrendIcon = (accuracy: number, weeklyAccuracy?: number) => {
    if (!weeklyAccuracy || weeklyAccuracy === 0) return <TrendingFlatIcon />;
    
    if (weeklyAccuracy > accuracy + 2) return <TrendingUpIcon />;
    if (weeklyAccuracy < accuracy - 2) return <TrendingDownIcon />;
    return <TrendingFlatIcon />;
  };

  const getTrendColor = (accuracy: number, weeklyAccuracy?: number): string => {
    if (!weeklyAccuracy || weeklyAccuracy === 0) return '#9e9e9e';
    if (weeklyAccuracy > accuracy + 2) return '#2e7d32';
    if (weeklyAccuracy < accuracy - 2) return '#d32f2f';
    return '#ed6c02';
  };

  const getInitials = (name: string = ''): string => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRankChipStyle = (index: number): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      marginTop: 4,
      height: 24,
      fontSize: '0.75rem',
      fontWeight: 600,
      padding: '0 8px',
    };

    if (index === 0) {
      return {
        ...baseStyle,
        background: 'linear-gradient(135deg, #FFD700, #FDB931)',
        color: '#000',
        border: 'none',
      };
    } else if (index === 1) {
      return {
        ...baseStyle,
        background: 'linear-gradient(135deg, #C0C0C0, #E0E0E0)',
        color: '#000',
        border: 'none',
      };
    } else if (index === 2) {
      return {
        ...baseStyle,
        background: 'linear-gradient(135deg, #CD7F32, #B87333)',
        color: '#fff',
        border: 'none',
      };
    }

    return {
      ...baseStyle,
      background: '#f8f9fa',
      color: '#6c757d',
      border: '1px solid #dee2e6',
    };
  };

  const uniqueLeagues = useMemo(() => {
    const leagues = new Set(entries.map(e => e.team.league).filter(l => l && l !== 'Unknown'));
    return Array.from(leagues);
  }, [entries]);

  // Calculate stats for display
  const totalPredictions = useMemo(() => 
    entries.reduce((sum, e) => sum + e.predictions, 0), [entries]
  );
  
  const totalCorrect = useMemo(() => 
    entries.reduce((sum, e) => sum + e.correct, 0), [entries]
  );

  const hasResults = totalCorrect > 0;

  return (
    <div className="leaderboard-container">
      <div className="leaderboard-paper">
        <div className="leaderboard-header">
          <div className="title-section">
            <h1 className="leaderboard-title">
              🏆 Prediction Leaderboard
            </h1>
            <DataSourceIndicator source={dataSource} />
          </div>

          {/* Data Collection Status Banner - Show when no results yet */}
          {!hasResults && entries.length > 0 && (
            <div className="data-collection-banner">
              <DataCollectionIcon />
              <span>
                📊 Collecting prediction results. Accuracy data will appear once matches are completed.
                Currently tracking {totalPredictions} predictions across {entries.length} teams.
              </span>
            </div>
          )}

          {/* Time Range Filter */}
          <div className="time-range-filter">
            <button
              className={`time-btn ${timeRange === 'all' ? 'active' : ''}`}
              onClick={() => setTimeRange('all')}
            >
              All Time
            </button>
            <button
              className={`time-btn ${timeRange === 'month' ? 'active' : ''}`}
              onClick={() => setTimeRange('month')}
            >
              This Month
            </button>
            <button
              className={`time-btn ${timeRange === 'week' ? 'active' : ''}`}
              onClick={() => setTimeRange('week')}
            >
              This Week
            </button>
          </div>

          {/* League Filter */}
          {uniqueLeagues.length > 1 && (
            <div className="league-filter">
              <select
                className="league-select"
                value={selectedLeague}
                onChange={(e) => setSelectedLeague(e.target.value)}
              >
                <option value="">All Leagues</option>
                {uniqueLeagues.map(league => (
                  <option key={league} value={league}>{league}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="stats-grid">
          <StatsCard 
            title="Total Predictions" 
            value={totalPredictions.toLocaleString()} 
            subvalue={`${entries.length} teams`}
            icon="📊"
            color="#4263eb"
          />
          
          <StatsCard 
            title="Correct Predictions" 
            value={totalCorrect.toLocaleString()} 
            subvalue={hasResults ? `${((totalCorrect / totalPredictions) * 100).toFixed(1)}% success rate` : 'Awaiting results'}
            icon="✅"
            color="#2e7d32"
            trend={hasResults ? 'up' : 'stable'}
          />
          
          <StatsCard 
            title="Overall Accuracy" 
            value={hasResults ? `${((totalCorrect / totalPredictions) * 100).toFixed(1)}%` : '—'} 
            subvalue={hasResults ? `Across ${entries.length} teams` : 'Data collection in progress'}
            icon="🎯"
            color={hasResults ? '#1976d2' : '#9e9e9e'}
          />
        </div>

        {/* Leaderboard Table */}
        <div className="table-container">
          <table className="leaderboard-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Team</th>
                <th className="text-center">League</th>
                <th className="text-center">Form</th>
                <th className="text-center">Predictions</th>
                <th className="text-center">Correct</th>
                <th className="text-center">Accuracy</th>
                <th className="text-center">Weekly</th>
                <th className="text-center">Trend</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="text-center loading-cell">
                    <div className="spinner"></div>
                    <p className="loading-text">Loading leaderboard data...</p>
                  </td>
                </tr>
              ) : entries.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center empty-cell">
                    <div className="empty-icon">📭</div>
                    <h3 className="empty-title">No leaderboard data available</h3>
                    <p className="empty-text">
                      {dataSource.source === 'sample' 
                        ? 'Connect to API to see real prediction statistics'
                        : 'Check back later for updated rankings'}
                    </p>
                  </td>
                </tr>
              ) : (
                entries.map((entry, index) => {
                  const accuracyColor = getAccuracyColor(entry.accuracy);
                  const trendIcon = getTrendIcon(entry.accuracy, entry.weekly_accuracy);
                  const trendColor = getTrendColor(entry.accuracy, entry.weekly_accuracy);
                  const showImage = entry.team.logo_url && !brokenImages.has(entry.team.id);
                  const accuracyMessage = getAccuracyMessage(entry.accuracy, entry.correct, entry.predictions);
                  
                  return (
                    <tr key={entry.team.id} className="leaderboard-row">
                      <td>
                        <RankBadge rank={index + 1} change={entry.rank_change} />
                      </td>
                      <td>
                        <div className="team-cell">
                          {showImage ? (
                            <img 
                              src={entry.team.logo_url} 
                              alt={entry.team.name}
                              className="team-avatar-img"
                              onError={() => handleImageError(entry.team.id)}
                              loading="lazy"
                            />
                          ) : (
                            <div className="team-avatar">
                              {getInitials(entry.team.name)}
                            </div>
                          )}
                          <div className="team-info">
                            <span className="team-name">
                              {entry.team.name}
                              {entry.correct > 0 && <VerifiedIcon />}
                            </span>
                            {entry.team.country && entry.team.country !== 'Unknown' && (
                              <span className="team-country">{entry.team.country}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="text-center">
                        {entry.team.league && entry.team.league !== 'Unknown' ? (
                          <span className="league-chip">
                            {entry.team.league}
                          </span>
                        ) : (
                          <span className="league-chip">—</span>
                        )}
                      </td>
                      <td className="text-center">
                        {entry.team.form && entry.team.form !== 'N/A' ? (
                          <span className="form-string" title="Last 5 matches">
                            {entry.team.form.split('').map((result, i) => (
                              <span
                                key={i}
                                className={`form-result ${result === 'W' ? 'win' : result === 'D' ? 'draw' : 'loss'}`}
                              >
                                {result}
                              </span>
                            ))}
                          </span>
                        ) : (
                          <span className="form-na">—</span>
                        )}
                      </td>
                      <td className="text-center">
                        <span className="stat-value">{entry.predictions}</span>
                      </td>
                      <td className="text-center">
                        <span className={`stat-value ${entry.correct > 0 ? 'success' : 'pending'}`}>
                          {entry.correct > 0 ? entry.correct : '—'}
                        </span>
                      </td>
                      <td className="text-center">
                        <div className="accuracy-container">
                          <span 
                            className="accuracy-value"
                            style={{ color: accuracyColor }}
                            title={accuracyMessage}
                          >
                            {getAccuracyIcon(entry.accuracy)} {entry.correct > 0 ? entry.accuracy.toFixed(1) : '—'}%
                          </span>
                          {index < 3 && entry.predictions > 0 && (
                            <Chip
                              label={`#${index + 1}`}
                              icon={<EmojiEventsIcon />}
                              style={getRankChipStyle(index)}
                              className="rank-chip"
                              tooltip={`${index + 1}st Place by predictions`}
                            />
                          )}
                        </div>
                      </td>
                      <td className="text-center">
                        {entry.weekly_predictions && entry.weekly_predictions > 0 ? (
                          <div className="weekly-stats">
                            <span className="weekly-count">{entry.weekly_predictions} pred</span>
                            {entry.weekly_accuracy && entry.weekly_accuracy > 0 && (
                              <span 
                                className="weekly-accuracy"
                                style={{ color: getAccuracyColor(entry.weekly_accuracy) }}
                              >
                                {entry.weekly_accuracy.toFixed(1)}%
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="weekly-na">—</span>
                        )}
                      </td>
                      <td className="text-center">
                        <span 
                          className="trend-icon"
                          style={{ color: trendColor }}
                          title={
                            entry.weekly_accuracy && entry.weekly_accuracy > 0
                              ? `Weekly accuracy: ${entry.weekly_accuracy.toFixed(1)}%`
                              : 'No weekly data'
                          }
                        >
                          {trendIcon}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Insights Section */}
        {!loading && entries.length > 0 && (
          <div className="insights-section">
            <h3 className="insights-title">📈 Key Insights</h3>
            <div className="insights-grid">
              <div className="insight-card">
                <span className="insight-icon">🏆</span>
                <div className="insight-content">
                  <span className="insight-label">Most Predictions</span>
                  <span className="insight-value">{entries[0].team.name}</span>
                  <span className="insight-detail">{entries[0].predictions} predictions</span>
                </div>
              </div>
              
              <div className="insight-card">
                <span className="insight-icon">📊</span>
                <div className="insight-content">
                  <span className="insight-label">Total Predictions</span>
                  <span className="insight-value">{totalPredictions}</span>
                  <span className="insight-detail">Across {entries.length} teams</span>
                </div>
              </div>
              
              <div className="insight-card">
                <span className="insight-icon">⚡</span>
                <div className="insight-content">
                  <span className="insight-label">Status</span>
                  <span className="insight-value">
                    {hasResults ? 'Results Available' : 'Collecting Data'}
                  </span>
                  <span className="insight-detail">
                    {hasResults 
                      ? `${totalCorrect} correct predictions` 
                      : 'Awaiting match completion'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer Note */}
        {!loading && entries.length > 0 && (
          <div className="footer-note">
            <InfoIcon />
            <span>
              {hasResults 
                ? 'Rankings based on prediction accuracy and total predictions made.'
                : 'Currently collecting prediction results. Accuracy data will appear once matches are completed.'}
              {dataSource.source === 'sample' && ' Showing demonstration data.'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(Leaderboard);